import React from 'react';
import { t } from '../../../utils/textUtils';
import {
  Box,
  Grid,
  TextField,
  Typography,
  MenuItem,
  FormHelperText,
  InputAdornment,
  Paper,
  Stack
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

const sumberDanaOptions = [
  'Pemerintah',
  'Swasta',
  'Internal'
];

const rencanaPembayaranOptions = [
  'Termin',
  'Sekali Bayar'
];

const BudgetForm = ({ formData, onChange, errors = {} }) => {
  const handleChange = (e) => {
    const { name, value } = e.target;
    onChange({ [name]: value });
  };

  // Format currency
  const formatCurrency = (value) => {
    if (!value) return '';
    return new Intl.NumberFormat('id-ID').format(value);
  };

  return (
    <Box>
      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          mb: 2.5,
          borderRadius: 2,
          borderColor: 'divider',
          bgcolor: '#F8FAFC'
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: '#ffffff',
              boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <AccountBalanceIcon sx={{ color: '#F59E0B' }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
              {t('BIAYA DAN PENDANAAN')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('Tentukan anggaran dan rencana pendanaan untuk proyek konstruksi prefabrikasi.')}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 2, sm: 3 }, 
            borderRadius: 2, 
            border: '1px solid',
            borderColor: 'rgba(15, 23, 42, 0.1)',
            bgcolor: '#FFFFFF',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e40af', mb: 0.5 }}>
              {t('ANGGARAN PROYEK')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('Tentukan anggaran dan rencana pendanaan untuk proyek konstruksi prefabrikasi.')}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('NILAI PROYEK')}
                name="totalAnggaran"
                type="number"
                value={formData.totalAnggaran}
                onChange={handleChange}
                error={!!errors.totalAnggaran}
                helperText={errors.totalAnggaran}
                InputProps={{
                  startAdornment: <InputAdornment position="start">Rp</InputAdornment>,
                  inputProps: { min: 0 }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#3B82F6'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3B82F6'
                    }
                  }
                }}
              />
              <FormHelperText sx={{ color: '#64748B', fontWeight: 500 }}>
                {formData.totalAnggaran ? t(`Terbilang: Rp ${formatCurrency(formData.totalAnggaran)}`) : ''}
              </FormHelperText>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label={t('SUMBER DANA')}
                name="sumberDana"
                value={formData.sumberDana}
                onChange={handleChange}
                error={!!errors.sumberDana}
                helperText={errors.sumberDana || t('Pilih sumber pendanaan proyek')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#3B82F6'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3B82F6'
                    }
                  }
                }}
              >
                {sumberDanaOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {t(option.toUpperCase())}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label={t('RENCANA PEMBAYARAN')}
                name="rencanaPembayaran"
                value={formData.rencanaPembayaran}
                onChange={handleChange}
                error={!!errors.rencanaPembayaran}
                helperText={errors.rencanaPembayaran || t('Pilih rencana pembayaran proyek')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#3B82F6'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3B82F6'
                    }
                  }
                }}
              >
                {rencanaPembayaranOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {t(option.toUpperCase())}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {formData.rencanaPembayaran === 'Termin' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('DETAIL RENCANA PEMBAYARAN')}
                  name="detailPembayaran"
                  multiline
                  rows={4}
                  value={formData.detailPembayaran || ''}
                  onChange={handleChange}
                  placeholder={t('CONTOH: TERMIN 1 (30%): SETELAH PENYELESAIAN PONDASI, TERMIN 2 (40%): SETELAH PEMASANGAN STRUKTUR, TERMIN 3 (30%): SETELAH SERAH TERIMA')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#3B82F6'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3B82F6'
                      }
                    }
                  }}
                />
                <FormHelperText sx={{ color: '#64748B' }}>
                  {t('Jelaskan detail pembayaran termin (persentase, milestone, dll)')}
                </FormHelperText>
              </Grid>
            )}
          </Grid>
        </Paper>
    </Box>
  );
};

export default BudgetForm;
